import type {
  PrecomputedEvaluationsInterface,
  StatsigPlugin,
} from '@statsig/client-core';
import type { StatsigClient } from '@statsig/js-client';
import {
  StatsigTriggeredSessionReplayPlugin,
  forceStartRecording,
} from '@statsig/session-replay';

export const DEFAULT_SESSION_REPLAY_GATE = 'enable-session-replay';

export type ReplayStartReason = 'dynamic_sampling' | 'error_override';

interface ReplayServiceOptions {
  sdkKey: string;
  samplingGateName?: string;
}

interface ReplayClient extends PrecomputedEvaluationsInterface {
  loadingStatus: PrecomputedEvaluationsInterface['loadingStatus'];
}

const replayPlugins: Array<StatsigPlugin<StatsigClient>> = [
  new StatsigTriggeredSessionReplayPlugin() as StatsigPlugin<StatsigClient>,
];

let replayClient: ReplayClient | null = null;
let replaySdkKey: string | null = null;
let samplingGateName = DEFAULT_SESSION_REPLAY_GATE;
let isClientReady = false;
let hasReplayStarted = false;
let hasSamplingBeenEvaluated = false;
let queuedStartReason: ReplayStartReason | null = null;

function isReady(client: ReplayClient | null): boolean {
  return client?.loadingStatus === 'Ready';
}

function resetReplayRuntime(): void {
  replayClient = null;
  isClientReady = false;
  hasReplayStarted = false;
  hasSamplingBeenEvaluated = false;
  queuedStartReason = null;
}

export function configureStatsigReplay(options: ReplayServiceOptions): void {
  const nextGate =
    options.samplingGateName?.trim() || DEFAULT_SESSION_REPLAY_GATE;
  if (replaySdkKey !== options.sdkKey || samplingGateName !== nextGate) {
    replaySdkKey = options.sdkKey;
    samplingGateName = nextGate;
    resetReplayRuntime();
  }
}

export function getStatsigReplayPlugins(): Array<
  StatsigPlugin<StatsigClient>
> {
  return replayPlugins;
}

function startReplayNow(reason: ReplayStartReason): void {
  if (hasReplayStarted || replaySdkKey == null) {
    return;
  }

  try {
    forceStartRecording(replaySdkKey);
    hasReplayStarted = true;
    queuedStartReason = null;
    replayClient?.logEvent('session_replay_started', undefined, { reason });
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : String(error);
    replayClient?.logEvent('session_replay_start_failed', undefined, {
      reason,
      error: errorMessage,
    });
  }
}

function flushQueuedStart(): void {
  if (!isClientReady || queuedStartReason == null) {
    return;
  }

  startReplayNow(queuedStartReason);
}

export function registerStatsigReplayClient(
  client: PrecomputedEvaluationsInterface | null,
): void {
  replayClient = client as ReplayClient | null;
  isClientReady = isReady(replayClient);
  flushQueuedStart();
}

export function evaluateDynamicReplaySampling(): void {
  if (hasSamplingBeenEvaluated || replayClient == null || !isClientReady) {
    return;
  }

  hasSamplingBeenEvaluated = true;
  if (replayClient.checkGate(samplingGateName)) {
    requestReplayStart('dynamic_sampling');
  }
}

export function requestReplayStart(reason: ReplayStartReason): void {
  if (hasReplayStarted) {
    return;
  }

  if (queuedStartReason == null || reason === 'error_override') {
    queuedStartReason = reason;
  }

  flushQueuedStart();
}
